import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { timeTrackingAPI, timeClockAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Clock, Play, Pause, Square, Loader2, Timer, Pencil, Trash2, CalendarDays,
  Coffee, ListChecks, Building2,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtHM = (mins) => {
  const m = Math.max(0, Math.round(mins || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h > 0 ? `${h}h ${r}m` : `${r}m`;
};
const fmtClock = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = String(Math.floor(s / 3600)).padStart(2, '0');
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const sec = String(s % 60).padStart(2, '0');
  return `${h}:${m}:${sec}`;
};
const timeOf = (iso) => (iso ? new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '—');
const dateEs = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }) : '—');

// Segundos netos trabajados de una sesión abierta (resta pausas, incl. la abierta)
function liveWorkedSeconds(session) {
  if (!session) return 0;
  const start = new Date(session.started_at).getTime();
  const now = Date.now();
  let paused = 0;
  (session.pauses || []).forEach((p) => {
    const ps = new Date(p.start).getTime();
    const pe = p.end ? new Date(p.end).getTime() : now;
    if (pe > ps) paused += pe - ps;
  });
  return Math.max(0, Math.floor((now - start - paused) / 1000));
}

function StatCard({ label, value, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-primary mt-1">{value}</p>
      </CardContent>
    </Card>
  );
}

export default function TimeTrackingPage() {
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null); // /time-tracking/me
  const [active, setActive] = useState(null); // sesión abierta (jornada)
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);

  // edición de jornada / log
  const [editSession, setEditSession] = useState(null);
  const [sessForm, setSessForm] = useState({ worked_minutes: '', note: '' });
  const [editLog, setEditLog] = useState(null);
  const [logMin, setLogMin] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await timeTrackingAPI.getMe();
      setMe(data);
      if (data.method === 'jornada') {
        const a = await timeClockAPI.getActive();
        setActive(a.session);
      } else {
        setActive(null);
      }
    } catch (e) {
      console.error('Error time tracking:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // reloj en vivo
  useEffect(() => {
    if (!active || active.status === 'ended') return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [active]);

  const method = me?.method || 'task';
  const isJornada = method === 'jornada';

  const workedSecs = useMemo(() => (active ? liveWorkedSeconds(active) : 0), [active, tick]);

  const doClock = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load();
    } catch (e) {
      toast.error(e.message || 'Acción no disponible');
    }
    setBusy(false);
  };

  const saveSessionEdit = async () => {
    const payload = {};
    if (sessForm.worked_minutes !== '') payload.worked_minutes = parseInt(sessForm.worked_minutes, 10);
    payload.note = sessForm.note || null;
    try {
      await timeClockAPI.editSession(editSession.id, payload);
      toast.success('Jornada corregida');
      setEditSession(null);
      load();
    } catch (e) { toast.error(e.message || 'No se pudo guardar'); }
  };

  const deleteSession = async (id) => {
    if (!window.confirm('¿Eliminar esta jornada?')) return;
    try { await timeClockAPI.deleteSession(id); toast.success('Jornada eliminada'); load(); }
    catch (e) { toast.error(e.message); }
  };

  const saveLogEdit = async (id) => {
    const mins = parseInt(logMin, 10);
    if (isNaN(mins) || mins < 0) { toast.error('Minutos no válidos'); return; }
    try { await timeTrackingAPI.updateLog(id, mins); toast.success('Registro actualizado'); setEditLog(null); load(); }
    catch (e) { toast.error(e.message); }
  };
  const deleteLog = async (id) => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    try { await timeTrackingAPI.deleteLog(id); toast.success('Registro eliminado'); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const t = me?.totals || {};

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="time-tracking-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Clock className="w-7 h-7 text-primary" /> Registro de Tiempo
          </h1>
          <p className="text-muted-foreground mt-1">
            {isJornada
              ? 'Fichas tu jornada (entrada, pausas y salida). La fecha se calcula sola.'
              : 'Tu tiempo se registra con el cronómetro de cada tarea, repartido por cliente.'}
          </p>
        </div>
        <Badge variant="outline" className="text-sm px-3 py-1.5" data-testid="tt-method-badge">
          {isJornada ? <Timer className="w-4 h-4 mr-1.5" /> : <ListChecks className="w-4 h-4 mr-1.5" />}
          Método: {isJornada ? 'Por jornada' : 'Por tarea'}
        </Badge>
      </div>

      {/* Totales */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Hoy" value={fmtHM(t.today_minutes)} testid="tt-total-today" />
        <StatCard label="Esta semana" value={fmtHM(t.week_minutes)} testid="tt-total-week" />
        <StatCard label="Este mes" value={fmtHM(t.month_minutes)} testid="tt-total-month" />
      </div>

      {/* ===== MÉTODO A: FICHAJE ===== */}
      {isJornada && (
        <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent" data-testid="clock-panel">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <p className="text-sm text-muted-foreground mb-1">
                  {!active ? 'Sin jornada en curso' : active.status === 'paused' ? 'En pausa' : 'Jornada activa'}
                </p>
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <span className={`w-3 h-3 rounded-full ${active ? (active.status === 'paused' ? 'bg-amber-500' : 'bg-emerald-500 animate-pulse') : 'bg-muted-foreground/30'}`} />
                  <span className="text-4xl font-bold tabular-nums" data-testid="clock-live">{fmtClock(workedSecs)}</span>
                </div>
                {active && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Entrada {timeOf(active.started_at)} · Pausa acumulada {fmtHM(active.paused_minutes)}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!active && (
                  <Button size="lg" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" onClick={() => doClock(timeClockAPI.start, 'Jornada iniciada')} data-testid="clock-start-btn">
                    <Play className="w-5 h-5 mr-2" /> Fichar entrada
                  </Button>
                )}
                {active && active.status === 'active' && (
                  <>
                    <Button size="lg" variant="outline" disabled={busy} onClick={() => doClock(timeClockAPI.pause, 'En pausa')} data-testid="clock-pause-btn">
                      <Coffee className="w-5 h-5 mr-2" /> Pausa
                    </Button>
                    <Button size="lg" disabled={busy} className="bg-red-600 hover:bg-red-700" onClick={() => doClock(timeClockAPI.stop, 'Jornada finalizada')} data-testid="clock-stop-btn">
                      <Square className="w-5 h-5 mr-2" /> Fichar salida
                    </Button>
                  </>
                )}
                {active && active.status === 'paused' && (
                  <>
                    <Button size="lg" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" onClick={() => doClock(timeClockAPI.resume, 'Reanudada')} data-testid="clock-resume-btn">
                      <Play className="w-5 h-5 mr-2" /> Reanudar
                    </Button>
                    <Button size="lg" disabled={busy} className="bg-red-600 hover:bg-red-700" onClick={() => doClock(timeClockAPI.stop, 'Jornada finalizada')} data-testid="clock-stop-btn">
                      <Square className="w-5 h-5 mr-2" /> Fichar salida
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== LISTADO ===== */}
      {isJornada ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><CalendarDays className="w-5 h-5 text-primary" /> Mis jornadas (este mes)</CardTitle>
            <CardDescription>Total del mes: <strong>{fmtHM(me?.range_total_minutes)}</strong>. Puedes corregir una jornada si te dejaste el reloj corriendo.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {(me?.sessions || []).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-10">Aún no has fichado ninguna jornada este mes.</p>
              )}
              {(me?.sessions || []).map((s) => (
                <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`session-row-${s.id}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    <span className="text-sm font-medium w-32 shrink-0 capitalize">{dateEs(s.date)}</span>
                    <span className="text-sm text-muted-foreground">{timeOf(s.started_at)} → {s.ended_at ? timeOf(s.ended_at) : <em>en curso</em>}</span>
                    {s.paused_minutes > 0 && <Badge variant="outline" className="text-[10px]"><Coffee className="w-3 h-3 mr-1" />{fmtHM(s.paused_minutes)} pausa</Badge>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-semibold text-primary" data-testid={`session-worked-${s.id}`}>{fmtHM(s.worked_minutes)}</span>
                    {s.status === 'ended' && (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => { setEditSession(s); setSessForm({ worked_minutes: String(s.worked_minutes), note: s.note || '' }); }} data-testid={`session-edit-${s.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => deleteSession(s.id)} data-testid={`session-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {(me?.by_client?.length > 0) && (
            <Card>
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Building2 className="w-5 h-5 text-primary" /> Por cliente (este mes)</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {me.by_client.map((c) => (
                  <div key={c.client_id} className="flex items-center justify-between text-sm" data-testid={`me-client-${c.client_id}`}>
                    <span className="text-muted-foreground">{c.client_name}</span>
                    <span className="font-medium">{fmtHM(c.minutes)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><ListChecks className="w-5 h-5 text-primary" /> Mis registros por tarea (este mes)</CardTitle>
              <CardDescription>Total del mes: <strong>{fmtHM(me?.range_total_minutes)}</strong>. Corrige un registro si te dejaste el cronómetro.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {(me?.entries || []).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-10">No hay tiempo registrado este mes. Usa el cronómetro (▶) dentro de cada tarea en <strong>Mis Tareas</strong>.</p>
                )}
                {(me?.entries || []).map((e) => (
                  <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm" data-testid={`log-row-${e.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-muted-foreground w-24 shrink-0">{e.date ? new Date(e.date + 'T00:00:00').toLocaleDateString('es-ES') : '—'}</span>
                      <span className="truncate">{e.task_title || 'Tarea'}</span>
                      {e.client_name && <Badge variant="outline" className="shrink-0">{e.client_name}</Badge>}
                    </div>
                    {editLog === e.id ? (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Input type="number" min="0" value={logMin} onChange={(ev) => setLogMin(ev.target.value)} className="h-8 w-20" data-testid={`log-input-${e.id}`} />
                        <span className="text-xs text-muted-foreground">min</span>
                        <Button variant="ghost" size="sm" className="h-7 text-emerald-600" onClick={() => saveLogEdit(e.id)} data-testid={`log-save-${e.id}`}>Guardar</Button>
                        <Button variant="ghost" size="sm" className="h-7" onClick={() => setEditLog(null)}>Cancelar</Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="font-semibold">{fmtHM(e.duration_minutes)}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={() => { setEditLog(e.id); setLogMin(String(e.duration_minutes)); }} data-testid={`log-edit-${e.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => deleteLog(e.id)} data-testid={`log-delete-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Dialog corregir jornada */}
      <Dialog open={!!editSession} onOpenChange={(o) => !o && setEditSession(null)}>
        <DialogContent data-testid="session-edit-dialog">
          <DialogHeader><DialogTitle>Corregir jornada · {editSession && dateEs(editSession.date)}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Minutos trabajados (netos)</Label>
              <Input type="number" min="0" value={sessForm.worked_minutes} onChange={(e) => setSessForm({ ...sessForm, worked_minutes: e.target.value })} data-testid="session-worked-input" />
              <p className="text-[11px] text-muted-foreground">Ajusta el total si te dejaste el reloj corriendo o hubo un despiste.</p>
            </div>
            <div className="space-y-2">
              <Label>Nota (opcional)</Label>
              <Input value={sessForm.note} onChange={(e) => setSessForm({ ...sessForm, note: e.target.value })} placeholder="Motivo de la corrección..." data-testid="session-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSession(null)}>Cancelar</Button>
            <Button onClick={saveSessionEdit} data-testid="session-save-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
